import { Flex, Modal, Select, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useCopyClientToBranchMutation,
  useGetBranchesWithoutClientQuery,
} from '@app/api/Client/api';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

import { useLeadCardContext } from '../../context';

const { Text } = Typography;

export const AddBranchModal = () => {
  const {
    isCreateBranchModalOpen,
    closeCreateBranchModal,
    branches,
    leadCardData,
    refetchLeadCard,
    selectBranch,
  } = useLeadCardContext();

  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const leadId = useMemo(
    () => leadCardData?.LeadId ?? null,
    [leadCardData?.LeadId]
  );

  const [selectedNewBranchId, setSelectedNewBranchId] = useState<number | null>(
    null
  );

  const {
    data: branchesData,
    isFetching: isBranchesFetching,
    error: branchesError,
    isError: isBranchesError,
  } = useGetBranchesWithoutClientQuery(
    { ActiveBranchId, leadId: leadId! },
    { skip: !isCreateBranchModalOpen || !leadId || !ActiveBranchId }
  );

  useErrorHandler([{ flag: isBranchesError, error: branchesError }]);

  const [copyClientToBranch, { isLoading: isCopyClientToBranchLoading }] =
    useCopyClientToBranchMutation();

  const attachedBranchIds = useMemo(
    () => new Set(branches.map(({ id }) => id)),
    [branches]
  );

  const branchOptions = useMemo(
    () =>
      (branchesData ?? [])
        .filter(({ Id }) => !attachedBranchIds.has(Id))
        .map(({ Id, Name }) => ({ value: Id, label: Name ?? '' })),
    [branchesData, attachedBranchIds]
  );

  const submitHandler = useCallback(async () => {
    if (leadId === null) return;
    if (selectedNewBranchId === null) {
      showNotification('Необходимо выбрать филиал');
      return;
    }

    try {
      await copyClientToBranch({
        ActiveBranchId,
        data: { LeadId: leadId, BranchId: selectedNewBranchId },
      }).unwrap();

      showNotification('Клиент успешно добавлен в филиал', 'success');
      closeCreateBranchModal();
      selectBranch(selectedNewBranchId);
      refetchLeadCard();
    } catch (error) {
      showNotification(error);
    }
  }, [
    leadId,
    selectedNewBranchId,
    copyClientToBranch,
    ActiveBranchId,
    showNotification,
    closeCreateBranchModal,
    selectBranch,
    refetchLeadCard,
  ]);

  useEffect(() => {
    if (isCreateBranchModalOpen) return;

    setSelectedNewBranchId(null);
  }, [isCreateBranchModalOpen]);

  return (
    <Modal
      title="Добавление клиента в филиал"
      okText="Сохранить"
      cancelText="Отменить"
      open={isCreateBranchModalOpen}
      onCancel={closeCreateBranchModal}
      onOk={submitHandler}
      okButtonProps={{
        loading: isCopyClientToBranchLoading,
        disabled: selectedNewBranchId === null,
      }}
      cancelButtonProps={{
        disabled: isCopyClientToBranchLoading,
      }}
      destroyOnHidden
    >
      <Flex gap={16} vertical>
        <Text>Выберите филиал, в котором Вы хотите создать клиента:</Text>
        <Select
          loading={isBranchesFetching}
          value={selectedNewBranchId}
          onChange={setSelectedNewBranchId}
          options={branchOptions}
          placeholder="Выберите филиал"
        />
      </Flex>
    </Modal>
  );
};
